import moment from "moment";

// TODO: Figure out why this import MUST be deeply imported
import { IDTypes } from "@btnet/services-btnet/handlers/models/iDTypes";

import { type ILanguageConstants } from "types/constants";
import { AccountingIntegrationType } from "types/enum";

export enum AccountingEntityTypes {
    TimeCard = 0,
    Bill = 1,
    ChangeOrder = 2,
    OwnerInvoice = 3,
    PurchaseOrderPayment = 4,
    CreditMemo = 5,
    VendorCredit = 6,
    Check = 7,
    Expense = 8,
    CreditCardCredit = 9,
    Deposit = 10,
}

export enum JobsiteLinkingTypes {
    Customer = 1,
    Job = 2,
    Project = 3,
}

export enum AccountingConnectionStatus {
    NotConnected = 0,
    PendingSync = 1,
    Connected = 2,
}

export enum SentToAccountingStatus {
    None = -1,
    NotSent = 0,
    Sent = 1,
    Pending = 2,
}

export enum TaxOptions {
    IgnoreTax = 0,
    TaxInclusive = 1,
    TaxExclusive = 2,
}

export const IntegrationTypeNames: Record<AccountingIntegrationType, string> = {
    [AccountingIntegrationType.None]: "N/A",
    [AccountingIntegrationType.QuickBooksDesktop]: "QuickBooks",
    [AccountingIntegrationType.QuickBooksOnline]: "QuickBooks",
    [AccountingIntegrationType.Xero]: "Xero",
    [AccountingIntegrationType.SageIntacct]: "Sage",
};

/**
 * Resolves an accounting integration type to its display name, guarding against
 * off-enum values the backend may send (Record indexing returns `string` even on a
 * runtime miss because `noUncheckedIndexedAccess` is off, so the fallback lives here).
 */
export function getIntegrationTypeName(type: AccountingIntegrationType): string {
    return IntegrationTypeNames[type] ?? "";
}

export const IntegrationTypeJobLinkingHelpServiceCloudUrls = new Map<
    AccountingIntegrationType,
    string
>([
    [AccountingIntegrationType.None, "N/A"],
    [
        AccountingIntegrationType.QuickBooksDesktop,
        "https://helpcenter.buildertrend.net/s/article/Quickbooks-Desktop-Initial-Integration",
    ],
    [
        AccountingIntegrationType.QuickBooksOnline,
        "https://helpcenter.buildertrend.net/s/article/Quickbooks-Online-Initial-Integration",
    ],
    [
        AccountingIntegrationType.Xero,
        "https://helpcenter.buildertrend.net/s/article/Xero-Initial-Integration",
    ],
]);

export const IntegrationPrimaryEntityName = new Map<AccountingIntegrationType, string>([
    [AccountingIntegrationType.None, "N/A"],
    [AccountingIntegrationType.QuickBooksDesktop, "Customer"],
    [AccountingIntegrationType.QuickBooksOnline, "Customer"],
    [AccountingIntegrationType.Xero, "Contact"],
]);

export const IntegrationSecondaryEntityName = new Map<AccountingIntegrationType, string>([
    [AccountingIntegrationType.None, ""],
    [AccountingIntegrationType.QuickBooksDesktop, "Customer & Job"],
    [AccountingIntegrationType.QuickBooksOnline, "Customer & Sub Customer"],
    [AccountingIntegrationType.Xero, ""],
]);

/**
 * The single frontend source for the QuickBooks Online outage copy, shared by every in-context
 * surface that swaps its message while QBO is degraded.
 *
 * The header banner's copy is owned separately, backend-side, where the `SystemAlert` is built - the
 * text lives on two runtimes, so "one place" is one constant per runtime, not one literal. Changing
 * the wording is two edits, not fifteen. Do not inline this string at an individual site.
 *
 * The wording must not tell users to reconnect, to contact Support, or that their account is broken:
 * none of those are true during an Intuit outage. It gives a concrete action (refresh) even though
 * refreshing does not actually resolve an Intuit-side outage, on the theory that a clear, actionable
 * message beats a technically-precise one users can't act on.
 */
export const QboOutageMessage =
    "QuickBooks Online is currently experiencing a service interruption. Some accounting actions — like invoicing or payment syncs — may fail or be delayed. Try refreshing the page in a few minutes.";

/**
 * Linking-card flavor of {@link QboOutageMessage}: names the specific entities that cannot be linked,
 * keyed by the same `IDTypes` the card already threads through for its other entity-specific copy.
 */
export const getQboOutageLinkingMessage = (idType: IDTypes): string => {
    let entityPhrase: string;
    switch (idType) {
        case IDTypes.JobID:
            entityPhrase = "Jobs and customers";
            break;
        case IDTypes.VendorID:
            entityPhrase = "Subs and vendors";
            break;
        case IDTypes.EmployeeID:
            entityPhrase = "Internal users";
            break;
        default:
            entityPhrase = "Entities";
    }

    return `QuickBooks Online is currently experiencing a service interruption. ${entityPhrase} cannot be linked to accounting at this time. Try refreshing the page in a few minutes.`;
};

/**
 * True when a failed accounting request carried `IsAccountingIntegrationDegraded`. The flag rides the
 * failure response itself, so an Intuit outage is distinguishable from a broken account without a
 * second call - which matters because the failure path is the only one the outage reaches, and by then
 * there is no successfully-loaded entity left to read the flag off of.
 */
export const isAccountingIntegrationDegradedError = (error: unknown): boolean => {
    const data = (error as { response?: { data?: unknown } })?.response?.data;

    return (
        typeof data === "object" &&
        data !== null &&
        (data as { isAccountingIntegrationDegraded?: boolean }).isAccountingIntegrationDegraded ===
            true
    );
};

export const AccountingConnectionStatusText = new Map<AccountingConnectionStatus, string>([
    [AccountingConnectionStatus.NotConnected, "Not Connected"],
    [AccountingConnectionStatus.PendingSync, "Pending Sync"],
    [AccountingConnectionStatus.Connected, "Connected"],
]);

export const EntityTypeNames = new Map<IDTypes, ILanguageConstants>([
    [
        IDTypes.VendorID,
        {
            singular: "Sub",
            plural: "Subs",
        },
    ],
    [
        IDTypes.EmployeeID,
        {
            singular: "Internal User",
            plural: "Internal Users",
        },
    ],
    [
        IDTypes.JobID,
        {
            singular: "Job",
            plural: "Jobs",
        },
    ],
    [
        IDTypes.CostCodeID,
        {
            singular: "Cost Code",
            plural: "Cost Codes",
        },
    ],
    [
        IDTypes.PaymentMethodBankAccount,
        {
            singular: "Bank Account",
            plural: "Bank Accounts",
        },
    ],
]);

export const getJobEntityNames = (type: AccountingIntegrationType) => {
    let entityNames = `${IntegrationPrimaryEntityName.get(type)}`;
    if (
        IntegrationSecondaryEntityName.get(type) &&
        IntegrationSecondaryEntityName.get(type) !== ""
    ) {
        entityNames = `${entityNames} or ${IntegrationSecondaryEntityName.get(type)}`;
    }
    return entityNames;
};

export class AccountingValidationResponse {
    constructor(data: any) {
        this.formMessage = data.formMessage;
        this.isAccountsPayableAccount = data.isAccountsPayableAccount;
        this.isJobsiteLinked = data.isJobsiteLinked;
        this.isVendorLinked = data.isVendorLinked;
        this.allCostCodesValid = data.allCostCodesValid;
        this.accountValidationErrorMsg = data.accountValidationErrorMsg;
        this.accountingValidationErrorAlertMessage = data.accountingValidationErrorAlertMessage;
        // Read through a narrowed view rather than off the `any` above: the surrounding assignments
        // are grandfathered no-unsafe-* suppressions, and adding to that count works against the
        // burn-down.
        this.isAccountingIntegrationDegraded = (
            data as { isAccountingIntegrationDegraded?: boolean }
        ).isAccountingIntegrationDegraded;
    }
    formMessage: string;
    isAccountsPayableAccount: boolean;
    isJobsiteLinked: boolean;
    isVendorLinked: boolean;
    allCostCodesValid: boolean;
    accountValidationErrorMsg?: string;
    accountingValidationErrorAlertMessage?: string;
    /**
     * True when the sync failed while the accounting integration was degraded. The specific validation
     * messages are misleading in that case - they describe a link or account problem the builder
     * cannot fix, when the real cause is on Intuit's side - so consumers swap in
     * {@link QboOutageMessage} instead of the message they would normally show.
     */
    isAccountingIntegrationDegraded?: boolean;
}

export class EntityAccountingStatus {
    constructor(data: any) {
        this.accountingTitle = data.accountingTitle;
        this.accountingImage = data.accountingImage;
        this.sendToAccountingText = data.sendToAccountingText;
        this.syncStatus = data.syncStatus;
        this.syncStatusTooltipMessage = data.syncStatusTooltipMessage;
        this.lastSync = data.lastSync;
        this.canSendToAccounting = data.canSendToAccounting;
        this.accountsPayableTerm = data.accountsPayableTerm;
        this.accountsReceivableTerm = data.accountsReceivableTerm;
        this.flatFeeTerm = data.flatFeeTerm;
        this.creditMemoStatus = data.creditMemoStatus;
        this.invoiceStatus = data.invoiceStatus;
        this.showExpanded = data.creditMemoStatus || data.invoiceStatus || data.depositStatus;
        this.totalAmount = data.totalAmount;
        this.amountPaid = data.amountPaid;
        this.paidDate = data.paidDateValue ? moment(data.paidDateValue) : undefined;
        this.showButton = data.showButton;
        this.showCheckbox = data.showCheckbox;
        this.supportsJobsitenumberegrationTypes = data.supportsJobsitenumberegrationTypes;
        this.paidDateValue = data.paidDateValue;
        this.outstandingBalanceText = data.outstandingBalanceText;
        this.totalAmountText = data.totalAmountText;
        this.totalTaxText = data.totalTaxText;
        this.helpMessageText = data.helpMessageText;
        this.linkedByName = data.linkedByName;
        this.syncButtonText = data.syncButtonText;
        this.hasExternalAccountingLink = data.hasExternalAccountingLink;
        this.externalAccountingLinkText = data.externalAccountingLinkText;
        this.externalAccountingLink = data.externalAccountingLink;
        this.showDisabledInputs = data.showDisabledInputs;
        this.depositStatus = data.depositStatus;
        this.canReset = data.canReset;
        this.resetButtonText = data.resetButtonText;
        this.resetPopoverText = data.resetPopoverText;
    }

    accountingTitle: string;
    accountingImage: string;
    sendToAccountingText: string;
    syncStatus: SentToAccountingStatus;
    syncStatusTooltipMessage?: string;
    lastSync: string;
    canSendToAccounting: boolean;
    accountsPayableTerm: string;
    accountsReceivableTerm: string;
    flatFeeTerm: string;
    creditMemoStatus: string;
    invoiceStatus: string;
    totalAmount: number;
    amountPaid?: number;
    paidDate?: moment.Moment;
    showButton: boolean;
    showCheckbox: boolean;
    supportsJobsitenumberegrationTypes: boolean;
    paidDateValue?: moment.Moment;
    outstandingBalanceText: string;
    totalAmountText: string;
    totalTaxText: string;
    helpMessageText: string;
    showExpanded: boolean;
    linkedByName: string;
    syncButtonText: string;
    hasExternalAccountingLink: boolean;
    externalAccountingLinkText: string;
    externalAccountingLink: string;
    showDisabledInputs?: boolean;
    depositStatus: string;
    canReset?: boolean;
    resetButtonText?: string;
    resetPopoverText?: string;
}

export class TaxValidationAlert {
    constructor(data: any) {
        this.confirmationMessage = data.confirmationMessage;
        this.confirmButtonText = data.confirmButtonText;
        this.cancelButtonText = data.cancelButtonText;
    }

    confirmationMessage: string;
    confirmButtonText: string;
    cancelButtonText: string;
}

export class TaxValidationAlertResponse {
    constructor(data: any) {
        this.taxValidationAlert = data.taxValidationAlert
            ? new TaxValidationAlert(data.taxValidationAlert)
            : undefined;
    }

    taxValidationAlert?: TaxValidationAlert;
}
